import { Route } from '@angular/router';
import { routes } from '@libs/utils';
import { pathParameter } from '@libs/shared-services';
import { PerioScreenComponentService } from 'libs/perio/src/lib/components/perio-screen/perio-screen.component.service';
import { activateGuard, baaRequestTokenGuard, baaSignedGuard, ffdNotesInvitationJoinRequestGuard, signBaaGuard } from '@libs/ffd-notes';
import { LoggedInGuard, LoggedOutGuard } from '../services/guards/auth.guards';
import { SiteAdminGuard } from '../services/guards/site-admin.guard';
import { DefaultSiteGuard, GlobalAdminGuard, NoSitesGuard, SiteAccessGuard } from '../services/guards/site.guard';
import { ShowcasePmsGuard } from '../services/guards/showcase-pms.guard';
import { TokenQueryParamAccessGuard } from '../services/guards/reset-password.guard';

import { ImagingDetailsService } from '../../../../../libs/imaging/src/lib/components/imaging-details/imaging-details.service';
import { MobileGuard } from '../services/guards/mobile.guard';
import { collabUnsentMessagesGuard } from '../services/guards/collab-unsent-messages.guard';
import { customerSuccessGuard } from '../services/guards/customer-success.guard';
import { supportCenterDictationGuard } from '../services/guards/support-center-dictation.guard';
import { SiteAssociationGuard } from '../services/guards/site-association.guard';
import { notesInprogressSavingGuard } from '../services/guards/notes-saving-in-progress.guard';
import { dynamicRouteMatcher } from './app.matchers';

export const mainRoutes: Route[] = [
  {
    path: routes.externalEmbed,
    children: [
      {
        path: routes.externalNotes,
        loadComponent: () => import('@libs/external-embed').then((m) => m.ExternalNotesComponent),
      },
      {
        // Standalone, public, iframe-embeddable FFD Notes all-at-once registration widget.
        // No guard: it must always render inside an external site's iframe regardless of any stale token.
        path: routes.externalFfdNotesRegistration,
        loadComponent: () => import('@libs/registration').then((m) => m.ExternalFfdNotesRegistrationComponent),
      },
    ],
  },
  {
    path: routes.getTheApp,
    children: [
      {
        path: '',
        loadComponent: () =>
          import('../mobile-redirect-screen/mobile-redirect-screen.component').then((m) => m.MobileRedirectScreenComponent),
      },
    ],
  },
  {
    path: routes.login,
    loadComponent: () => import('../login/login-layout/login-layout.component').then((m) => m.LoginLayoutComponent),
    canActivate: [LoggedOutGuard, MobileGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('../login/login-screen/login-screen.component').then((m) => m.LoginScreenComponent),
      },
      {
        path: routes.loginRegistered,
        loadComponent: () =>
          import('../login/registered-login-screen/registered-login-screen.component').then((m) => m.RegisteredLoginScreenComponent),
      },
      {
        path: routes.loginVerification,
        loadComponent: () =>
          import('../login/verification-screen/verification-screen.component').then((m) => m.VerificationScreenComponent),
      },
      {
        path: routes.loginLockout,
        children: [
          {
            path: routes.loginUnlockAccount,
            loadComponent: () =>
              import('../login/lockout-screen/components/unlock-account/unlock-account.component').then((m) => m.UnlockAccountComponent),
          },
          {
            path: routes.loginVerificationFailed,
            canActivate: [TokenQueryParamAccessGuard],
            loadComponent: () =>
              import('../login/lockout-screen/components/verification-failed/verification-failed.component').then(
                (m) => m.VerificationFailedComponent,
              ),
          },
        ],
      },
      {
        path: '**',
        redirectTo: routes.login,
      },
    ],
  },
  {
    path: `${routes.login}/${routes.loginSetup}`,
    canActivate: [LoggedOutGuard],
    loadComponent: () => import('../login/setup-login-screen/setup-login-screen.component').then((m) => m.SetupLoginScreenComponent),
  },
  {
    path: routes.forgotPassword,
    loadComponent: () =>
      import('../forgot-password/forgot-password-layout/forgot-password-layout.component').then((m) => m.ForgotPasswordLayoutComponent),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('../forgot-password/forgot-password-screen/forgot-password-screen.component').then((m) => m.ForgotPasswordScreenComponent),
      },
      {
        path: routes.resetPassword,
        canActivate: [TokenQueryParamAccessGuard],
        loadComponent: () =>
          import('../forgot-password/reset-password-screen/reset-password-screen.component').then((m) => m.ResetPasswordScreenComponent),
      },
    ],
  },
  {
    path: routes.registration,
    loadComponent: () => import('@libs/registration').then((m) => m.RegistrationComponent),
    canActivate: [LoggedOutGuard],
    children: [
      {
        path: routes.registrationEmailStep,
        loadComponent: () => import('@libs/registration').then((m) => m.RegistrationEmailStepComponent),
      },
      {
        path: routes.registrationOtpStep,
        loadComponent: () => import('@libs/registration').then((m) => m.RegistrationOtpStepComponent),
      },
      {
        path: routes.registrationProfileDetailsStep,
        loadComponent: () => import('@libs/registration').then((m) => m.RegistrationProfileDetailsStepComponent),
      },
      {
        path: '**',
        redirectTo: routes.registrationEmailStep,
      },
    ],
  },
  {
    path: routes.siteAssociation,
    loadComponent: () => import('@libs/site-association').then((m) => m.SiteAssociationScreenComponent),
    canActivate: [SiteAssociationGuard],
    children: [
      {
        path: routes.siteAssociationLogin,
        loadComponent: () => import('@libs/site-association').then((m) => m.SiteAssociationLoginComponent),
      },
      {
        path: routes.siteAssociationRegister,
        loadComponent: () => import('@libs/site-association').then((m) => m.SiteAssociationRegisterComponent),
      },
      {
        path: routes.siteAssociationOtp,
        loadComponent: () => import('@libs/site-association').then((m) => m.SiteAssociationOtpComponent),
      },
      {
        path: routes.siteAssociationOtpFailed,
        loadComponent: () => import('@libs/site-association').then((m) => m.SiteAssociationOtpFailedComponent),
      },
      {
        path: routes.siteAssociationInvitedEmailOtp,
        loadComponent: () => import('@libs/site-association').then((m) => m.SiteAssociationInvitedEmailOtpComponent),
      },
      {
        path: routes.siteAssociationInvitedEmailOtpFailed,
        loadComponent: () => import('@libs/site-association').then((m) => m.SiteAssociationInvitedEmailOtpFailedComponent),
      },
      {
        path: routes.siteAssociationVerifyInviteCode,
        loadComponent: () => import('@libs/site-association').then((m) => m.SiteAssociationInviteCodeComponent),
      },
      {
        path: routes.siteAssociationVerifyCodes,
        loadComponent: () => import('@libs/site-association').then((m) => m.SiteAssociationVerifyCodesComponent),
      },
      {
        path: routes.siteAssociationEaglesoftPassword,
        loadComponent: () => import('@libs/site-association').then((m) => m.SiteAssociationEaglesoftPasswordComponent),
      },
      {
        path: routes.siteAssociationProfileDetails,
        loadComponent: () => import('@libs/site-association').then((m) => m.SiteAssociationProfileDetailsComponent),
      },
      {
        path: routes.siteAssociationPatientPassword,
        loadComponent: () => import('@libs/site-association').then((m) => m.SiteAssociationPatientDetailsComponent),
        data: { withPassword: true },
      },
      {
        path: routes.siteAssociationDateOfBirth,
        loadComponent: () => import('@libs/site-association').then((m) => m.SiteAssociationPatientDetailsComponent),
        data: { withPassword: false },
      },
      {
        path: routes.siteAssociationOtpLockout,
        loadComponent: () => import('@libs/site-association').then((m) => m.SiteAssociationLockoutComponent),
      },
      {
        path: routes.siteAssociationVerifyInviteCodeLockout,
        loadComponent: () => import('@libs/site-association').then((m) => m.SiteAssociationInviteCodeLockoutComponent),
      },
      {
        path: routes.siteAssociationInvalidToken,
        loadComponent: () => import('@libs/site-association').then((m) => m.SiteAssociationTokenInvalidComponent),
      },
      {
        path: '**',
        loadComponent: () => import('@libs/site-association').then((m) => m.SiteAssociationLoginComponent),
      },
    ],
  },
  {
    path: `${routes.setup}/external-sign`,
    loadComponent: () => import('@libs/site-setup').then((m) => m.ExternalSignPageComponent),
    children: [
      {
        path: routes.form,
        loadComponent: () => import('@libs/site-setup').then((m) => m.ExternalSignFormComponent),
      },
      {
        path: routes.reject,
        loadComponent: () => import('@libs/site-setup').then((m) => m.RejectSignComponent),
      },
      {
        path: routes.help,
        loadComponent: () => import('@libs/site-setup').then((m) => m.SiteInstallHelpComponent),
      },
      {
        path: '**',
        redirectTo: routes.form,
      },
    ],
  },
  {
    path: routes.setup,
    loadComponent: () => import('@libs/site-setup').then((m) => m.SiteSetupComponent),
    canActivate: [LoggedInGuard],
    children: [
      {
        path: routes.compatibility,
        loadComponent: () => import('@libs/site-setup').then((m) => m.CompatibilityComponent),
      },
      // {
      //   path: routes.overview,
      //   loadComponent: () => import('@libs/site-setup').then((m) => m.OverviewComponent),
      // }, // fast pass - step skipped
      {
        path: routes.softwareLicense,
        loadComponent: () => import('@libs/site-setup').then((m) => m.SoftwareLicenseComponent),
        children: [
          // {
          //   path: `${routes.softwareLicenseVideo}`,
          //   loadComponent: () => import('@libs/site-setup').then((m) => m.SoftwareLicenseVideoComponent),
          // }, // fast pass 2 - step skipped
          {
            path: `${routes.softwareLicenseAgreement}`,
            loadComponent: () => import('@libs/site-setup').then((m) => m.SoftwareLicenseAgreementComponent),
          },
          {
            path: '**',
            redirectTo: routes.softwareLicenseAgreement,
          },
        ],
      },
      {
        path: routes.waitlistBlocker,
        loadComponent: () => import('@libs/site-setup').then((m) => m.InstallationPendingComponent),
      },
      {
        path: routes.serverInstallation,
        loadComponent: () => import('@libs/site-setup').then((m) => m.ServerInstallationComponent),
        children: [
          // {
          //   path: `${routes.serverInstallationVideo}`,
          //   loadComponent: () => import('@libs/site-setup').then((m) => m.ServerInstallationVideoComponent),
          // }, // Fast pass 2 - step skipped
          {
            path: `${routes.checklist}`,
            loadComponent: () => import('@libs/site-setup').then((m) => m.ServerInstallationCheckListComponent),
          },
          {
            path: `${routes.download}`,
            loadComponent: () => import('@libs/site-setup').then((m) => m.ServerInstallationDownloadComponent),
          },
          {
            path: '**',
            redirectTo: routes.checklist,
          },
        ],
      },

      {
        path: routes.practiceManager,
        loadComponent: () => import('@libs/site-setup').then((m) => m.PracticeManagerComponent),
      },
      {
        path: routes.itProviderDetails,
        loadComponent: () => import('@libs/site-setup').then((m) => m.ItProviderDetailsComponent),
      },
      {
        path: routes.installOptions,
        loadComponent: () => import('@libs/site-setup').then((m) => m.InstallOptionsComponent),
      },
      {
        path: routes.itProviderContacted,
        loadComponent: () => import('@libs/site-setup').then((m) => m.ItProviderContactedComponent),
      },
      {
        path: routes.help,
        loadComponent: () => import('@libs/site-setup').then((m) => m.SiteInstallHelpComponent),
      },
    ],
  },
  {
    path: routes.supportCenter,
    loadComponent: () => import('../components/layout/layout.component').then((m) => m.LayoutComponent),
    canActivate: [LoggedInGuard, SiteAccessGuard],
    children: [
      {
        path: '',
        canActivate: [MobileGuard],
        canDeactivate: [supportCenterDictationGuard],
        loadComponent: () => import('../support-center/support-center.component').then((m) => m.SupportCenterComponent),
      },
    ],
  },
  {
    path: '',
    redirectTo: routes.site,
    pathMatch: 'full',
  },
  {
    path: `${routes.ffdNotes}/${routes.login}`,
    loadComponent: () => import('../login/login-layout/login-layout.component').then((m) => m.LoginLayoutComponent),
    canActivate: [LoggedOutGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('../login/login-screen/login-screen.component').then((m) => m.LoginScreenComponent),
      },
      {
        path: routes.loginVerification,
        loadComponent: () =>
          import('../login/verification-screen/verification-screen.component').then((m) => m.VerificationScreenComponent),
      },
      {
        path: routes.loginLockout,
        children: [
          {
            path: routes.loginUnlockAccount,
            loadComponent: () =>
              import('../login/lockout-screen/components/unlock-account/unlock-account.component').then((m) => m.UnlockAccountComponent),
          },
          {
            path: routes.loginVerificationFailed,
            canActivate: [TokenQueryParamAccessGuard],
            loadComponent: () =>
              import('../login/lockout-screen/components/verification-failed/verification-failed.component').then(
                (m) => m.VerificationFailedComponent,
              ),
          },
        ],
      },
      {
        path: '**',
        redirectTo: routes.login,
      },
    ],
  },
  {
    path: `${routes.ffdNotes}/${routes.forgotPassword}`,
    loadComponent: () =>
      import('../forgot-password/forgot-password-layout/forgot-password-layout.component').then((m) => m.ForgotPasswordLayoutComponent),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('../forgot-password/forgot-password-screen/forgot-password-screen.component').then((m) => m.ForgotPasswordScreenComponent),
      },
      {
        path: routes.resetPassword,
        canActivate: [TokenQueryParamAccessGuard],
        loadComponent: () =>
          import('../forgot-password/reset-password-screen/reset-password-screen.component').then((m) => m.ResetPasswordScreenComponent),
      },
    ],
  },
  {
    path: `${routes.ffdNotes}/${routes.getTheApp}`,
    loadComponent: () => import('../mobile-redirect-screen/mobile-redirect-screen.component').then((m) => m.MobileRedirectScreenComponent),
  },
  {
    path: `${routes.ffdNotes}/${routes.baaRequestFlow}`,
    canActivate: [baaRequestTokenGuard],
    loadComponent: () => import('@libs/ffd-notes').then((m) => m.BaaRequestFlowComponent),
  },
  {
    path: `${routes.ffdNotes}/${routes.ffdNotesInvite}`,
    canActivate: [ffdNotesInvitationJoinRequestGuard],
    loadComponent: () => import('@libs/ffd-notes').then((m) => m.FfdNotesInvitationLandingComponent),
  },
  {
    path: `${routes.ffdNotes}/${routes.ffdNotesInviteSuccess}`,
    canActivate: [baaRequestTokenGuard],
    loadComponent: () => import('@libs/ffd-notes').then((m) => m.FfdNotesInviteSuccessComponent),
  },
  {
    path: `${routes.ffdNotes}/${routes.baaSignupFlow}`,
    canActivate: [baaRequestTokenGuard],
    loadComponent: () => import('@libs/ffd-notes').then((m) => m.BaaSignupFlowComponent),
    children: [
      {
        path: routes.baaSignupInfo,
        loadComponent: () => import('@libs/ffd-notes').then((m) => m.BaaSignupInfoComponent),
      },
      {
        path: routes.baaSignupAccountDetails,
        loadComponent: () => import('@libs/ffd-notes').then((m) => m.BaaSignupAccountDetailsComponent),
      },
      {
        path: routes.baaSignupOtp,
        loadComponent: () => import('@libs/ffd-notes').then((m) => m.BaaSignupOtpComponent),
      },
      {
        path: routes.baaSignupPracticeDetails,
        loadComponent: () => import('@libs/ffd-notes').then((m) => m.BaaSignupPracticeDetailsComponent),
      },
      {
        path: routes.baaSignupLogin,
        loadComponent: () => import('@libs/ffd-notes').then((m) => m.BaaSignupLoginComponent),
      },
      {
        path: '**',
        redirectTo: routes.baaSignupInfo,
      },
    ],
  },
  {
    path: routes.ffdNotes,
    loadComponent: () => import('@libs/ffd-notes').then((m) => m.FfdNotesLayoutComponent),
    canActivate: [LoggedInGuard, MobileGuard],
    children: [
      {
        path: routes.myAccount,
        canActivate: [MobileGuard],
        loadComponent: () => import('@libs/my-account').then((m) => m.MyAccountScreenComponent),
      },
      {
        path: routes.ffdNotesSignBaa,
        canActivate: [baaSignedGuard],
        loadComponent: () => import('@libs/ffd-notes').then((m) => m.FfdNotesSignBaaComponent),
      },
      {
        path: routes.ffdNotesActivate,
        canActivate: [activateGuard],
        loadComponent: () => import('@libs/ffd-notes').then((m) => m.FfdNotesActivateComponent),
      },
      {
        path: '',
        canActivate: [signBaaGuard],
        loadComponent: () => import('@libs/ffd-notes').then((m) => m.FfdNotesScreenComponent),
      },
    ],
  },
  {
    path: routes.site,
    loadComponent: () => import('@libs/home').then((m) => m.HomeScreenComponent),
    canActivate: [LoggedInGuard, DefaultSiteGuard, MobileGuard],
  },
  {
    // Users without any site have no siteId, so they get the collab screen outside of /site/:siteId.
    path: routes.allCollaborations,
    loadComponent: () => import('../components/layout/layout.component').then((m) => m.LayoutComponent),
    canActivate: [LoggedInGuard, NoSitesGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('@libs/collaboration').then((m) => m.CollabScreenComponent),
        pathMatch: 'full',
        canActivate: [MobileGuard],
        canDeactivate: [collabUnsentMessagesGuard],
      },
    ],
  },
  {
    path: `${routes.site}/:siteId`,
    loadComponent: () => import('../components/layout/layout.component').then((m) => m.LayoutComponent),
    canActivate: [LoggedInGuard, SiteAccessGuard],
    children: [
      {
        path: routes.home,
        loadComponent: () => import('@libs/home').then((m) => m.HomeScreenComponent),
        canActivate: [MobileGuard, ShowcasePmsGuard],
      },
      {
        path: routes.patients,
        loadComponent: () => import('../patients/patients.component').then((m) => m.PatientsComponent),
        pathMatch: 'full',
        canActivate: [MobileGuard],
      },
      {
        path: routes.allCollaborations,
        loadComponent: () => import('@libs/collaboration').then((m) => m.CollabScreenComponent),
        pathMatch: 'full',
        canActivate: [MobileGuard],
        canDeactivate: [collabUnsentMessagesGuard],
      },
      {
        path: `${routes.patients}/:patientId`,
        loadComponent: () => import('../clinical-tools/clinical-tools.component').then((m) => m.ClinicalToolsComponent),
        children: [
          {
            path: routes.charting,
            loadComponent: () => import('@libs/charting').then((m) => m.ChartingScreenComponent),
            children: [
              {
                path: `${routes.toothDetails}/:toothPosition`,
                loadComponent: () => import('@libs/charting').then((m) => m.ToothDetailsDialogContainerComponent),
                pathMatch: 'full',
              },
            ],
          },
          {
            path: routes.imaging,
            loadComponent: () => import('@libs/imaging').then((m) => m.ImagingScreenComponent),
            pathMatch: 'full',
          },
          {
            path: routes.imaging,
            loadComponent: () => import('@libs/imaging').then((m) => m.ImagingDetailsComponent),
            children: [
              {
                path: routes.fullSeries,
                loadComponent: () => import('@libs/imaging').then((m) => m.FullSeriesScreenComponent),
                pathMatch: 'full',
              },
              {
                matcher: dynamicRouteMatcher([routes.fullSeries, routes.panoramics, routes.photosAndVideos], 'imagingCategory'),
                loadComponent: () => import('@libs/imaging').then((m) => m.ImagingGalleryScreenComponent),
              },
              {
                path: routes.scans,
                loadComponent: () => import('@libs/imaging').then((m) => m.ImagingScansScreenComponent),
              },
              {
                path: `${routes.scans}/${routes.cbct}`,
                loadComponent: () => import('@libs/imaging').then((m) => m.CbctViewerComponent),
                pathMatch: 'full',
              },
              {
                path: `${routes.scans}/${routes.three_d}`,
                loadComponent: () => import('@libs/imaging/3d').then((m) => m.ThreeDImageComponent),
                pathMatch: 'full',
              },
              {
                matcher: dynamicRouteMatcher([routes.fullSeries, routes.panoramics, routes.photosAndVideos], 'imagingCategory'),
                providers: [ImagingDetailsService],
                loadComponent: () => import('@libs/imaging').then((m) => m.ImagingGalleryPreviewComponent),
                children: [
                  {
                    path: `${routes.teethGroup}/:${pathParameter('teethRange')}/:${pathParameter('imageId')}`,
                    loadComponent: () => import('@libs/imaging').then((m) => m.ImagingGalleryPreviewComponent),
                    children: [
                      {
                        matcher: dynamicRouteMatcher(['', routes.edit, routes.compare]),
                        loadComponent: () => import('@libs/imaging').then((m) => m.ImagingGalleryPreviewComponent),
                        pathMatch: 'full',
                      },
                    ],
                  },
                  {
                    path: `${routes.date}/:${pathParameter('date')}/:${pathParameter('teethRange')}/:${pathParameter('imageId')}`,
                    loadComponent: () => import('@libs/imaging').then((m) => m.ImagingGalleryPreviewComponent),
                    children: [
                      {
                        matcher: dynamicRouteMatcher(['', routes.edit, routes.compare]),
                        loadComponent: () => import('@libs/imaging').then((m) => m.ImagingGalleryPreviewComponent),
                        pathMatch: 'full',
                      },
                      {
                        path: '**',
                        redirectTo: '',
                      },
                    ],
                  },
                  {
                    path: `${routes.date}/:${pathParameter('date')}/:${pathParameter('imageId')}`,
                    loadComponent: () => import('@libs/imaging').then((m) => m.ImagingGalleryPreviewComponent),
                    children: [
                      {
                        matcher: dynamicRouteMatcher(['', routes.edit, routes.compare]),
                        loadComponent: () => import('@libs/imaging').then((m) => m.ImagingGalleryPreviewComponent),
                        pathMatch: 'full',
                      },
                      {
                        path: '**',
                        redirectTo: '',
                      },
                    ],
                  },
                  {
                    path: `:${pathParameter('imageId')}`,
                    loadComponent: () => import('@libs/imaging').then((m) => m.ImagingGalleryPreviewComponent),
                    children: [
                      {
                        matcher: dynamicRouteMatcher(['', routes.edit, routes.compare]),
                        loadComponent: () => import('@libs/imaging').then((m) => m.ImagingGalleryPreviewComponent),
                        pathMatch: 'full',
                      },
                      {
                        path: '**',
                        redirectTo: '',
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: routes.perio,
            providers: [PerioScreenComponentService],
            children: [
              {
                path: '',
                loadComponent: () => import('@libs/perio').then((m) => m.PerioScreenComponent),
                children: [
                  {
                    path: `${routes.toothDetails}/:${pathParameter('toothPosition')}`,
                    loadComponent: () => import('@libs/perio').then((m) => m.PerioToothDetailsDialogContainerComponent),
                    pathMatch: 'full',
                  },
                ],
              },
              {
                path: `${routes.editExam}/:${pathParameter('examId')}`,
                loadComponent: () => import('@libs/perio').then((m) => m.PerioExamScreenComponent),
                pathMatch: 'full',
              },
              {
                path: `${routes.newExam}`,
                loadComponent: () => import('@libs/perio').then((m) => m.PerioExamScreenComponent),
                pathMatch: 'full',
              },
            ],
          },
          {
            path: routes.notes,
            loadComponent: () => import('@libs/notes').then((m) => m.NotesScreenComponent),
            pathMatch: 'full',
            canDeactivate: [notesInprogressSavingGuard],
          },
          {
            path: routes.collab,
            canDeactivate: [collabUnsentMessagesGuard],
            loadComponent: () => import('@libs/collaboration').then((m) => m.CollabScreenComponent),
            pathMatch: 'full',
          },
          {
            path: '**',
            redirectTo: routes.charting,
          },
        ],
      },
      {
        path: routes.siteAdmin,
        canActivate: [MobileGuard],
        children: [
          {
            path: routes.practiceManagement,
            loadComponent: () => import('@libs/site-admin').then((m) => m.PracticeManagementScreenComponent),
            children: [
              {
                path: routes.practiceUserManagement,
                loadComponent: () => import('@libs/site-admin').then((m) => m.PracticeUserManagementComponent),
              },
              {
                path: routes.practiceDiscovery,
                loadComponent: () => import('@libs/site-admin').then((m) => m.PracticeDiscoveryComponent),
              },
              {
                path: routes.practiceDiscoveryEmbed,
                loadComponent: () => import('@libs/site-admin').then((m) => m.PracticeDiscoveryEmbedComponent),
              },
              {
                path: routes.patientManagement,
                loadComponent: () => import('@libs/site-admin').then((m) => m.PatientManagementComponent),
              },
              {
                path: routes.issueManagement,
                loadComponent: () => import('@libs/site-admin').then((m) => m.IssueManagementComponent),
              },
              {
                path: routes.deepgram,
                loadComponent: () => import('@libs/site-admin').then((m) => m.DeepgramSettingsComponent),
              },
              {
                path: routes.practiceSettings,
                canActivate: [SiteAdminGuard],
                children: [
                  {
                    path: '',
                    pathMatch: 'full',
                    loadComponent: () => import('@libs/site-admin').then((m) => m.PracticeSettingsComponent),
                  },
                  {
                    path: routes.operatoryOrder,
                    loadComponent: () => import('@libs/site-admin').then((m) => m.OperatoryOrderComponent),
                  },
                ],
              },
              {
                path: routes.billing,
                loadComponent: () => import('@libs/site-admin').then((m) => m.BillingTabComponent),
              },
            ],
          },
          {
            path: routes.bulkInvite,
            loadComponent: () => import('@libs/site-admin').then((m) => m.BulkInviteScreenComponent),
          },
        ],
      },
      {
        path: routes.myAccount,
        canActivate: [MobileGuard],
        loadComponent: () => import('@libs/my-account').then((m) => m.MyAccountScreenComponent),
      },
      {
        path: routes.supportCenter,
        canActivate: [MobileGuard],
        canDeactivate: [supportCenterDictationGuard],
        loadComponent: () => import('../support-center/support-center.component').then((m) => m.SupportCenterComponent),
      },
      {
        path: '**',
        redirectTo: routes.home,
      },
    ],
  },
  {
    path: routes.ffdAdmin,
    loadComponent: () => import('../components/admin-layout/admin-layout.component').then((m) => m.AdminLayoutComponent),
    canActivate: [LoggedInGuard, GlobalAdminGuard, MobileGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('@libs/ffd-admin').then((m) => m.FfdAdminScreenComponent),
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: routes.treatmentDatabase,
          },
          { path: routes.treatmentDatabase, loadComponent: () => import('@libs/ffd-admin').then((m) => m.TreatmentDatabaseComponent) },
          { path: routes.featureFlags, loadComponent: () => import('@libs/ffd-admin').then((m) => m.FeatureFlagsComponent) },
          { path: routes.syncAudits, loadComponent: () => import('@libs/ffd-admin').then((m) => m.SyncAuditsComponent) },
          { path: routes.usageTracking, loadComponent: () => import('@libs/ffd-admin').then((m) => m.UsageTrackingComponent) },
          {
            path: `${routes.usageTracking}/:siteId`,
            loadComponent: () => import('@libs/ffd-admin').then((m) => m.UsageTrackingSiteComponent),
          },
          { path: routes.tokenManager, loadComponent: () => import('@libs/ffd-admin').then((m) => m.TokenManagerComponent) },
          {
            path: routes.userManagement,
            loadComponent: () => import('@libs/ffd-admin').then((m) => m.UserManagementComponent),
          },
          {
            path: routes.supportInbox,
            canDeactivate: [collabUnsentMessagesGuard],
            loadComponent: () => import('@libs/collaboration').then((m) => m.CollabScreenComponent),
            data: { supportInbox: true },
          },
          {
            path: routes.personalAssistantInbox,
            canDeactivate: [collabUnsentMessagesGuard],
            loadComponent: () => import('@libs/collaboration').then((m) => m.CollabScreenComponent),
            data: { personalAssistantInbox: true },
          },
          {
            path: routes.quickActionManager,
            loadComponent: () => import('@libs/ffd-admin').then((m) => m.QuickActionManagerComponent),
          },
          {
            path: routes.assistantSettings,
            canActivate: [customerSuccessGuard],
            loadComponent: () => import('@libs/ffd-admin').then((m) => m.AssistantSettingsComponent),
          },
        ],
      },
    ],
  },
  {
    // Alias for the FFD Notes activation deep link / QR code
    path: 'mobile-notes/baa-activation',
    redirectTo: routes.ffdNotes,
  },
  {
    path: 'mobile-notes/baa-activation/:userId',
    redirectTo: routes.ffdNotes,
  },
  {
    // Alias for the FFD Notes BAA sign-up flow
    path: 'universal-notes/baa-sign',
    redirectTo: `${routes.ffdNotes}/${routes.ffdNotesSignBaa}`,
  },
  {
    path: 'mobile-notes/baa-sign',
    redirectTo: `${routes.ffdNotes}/${routes.ffdNotesSignBaa}`,
  },
  {
    // Alias for FFD Notes
    path: 'universal-notes',
    redirectTo: routes.ffdNotes,
  },
  {
    path: 'mobile-notes',
    redirectTo: routes.ffdNotes,
  },
  {
    // Redirect all other routes to the site instead of 404
    path: '**',
    redirectTo: routes.site,
  },
];

export const notesOnlyRoutes: Route[] = [
  {
    path: routes.login,
    loadComponent: () => import('../login/login-layout/login-layout.component').then((m) => m.LoginLayoutComponent),
    canActivate: [LoggedOutGuard, MobileGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('../login/login-screen/login-screen.component').then((m) => m.LoginScreenComponent),
      },
      {
        path: routes.loginVerification,
        loadComponent: () =>
          import('../login/verification-screen/verification-screen.component').then((m) => m.VerificationScreenComponent),
      },
      {
        path: routes.loginLockout,
        children: [
          {
            path: routes.loginUnlockAccount,
            loadComponent: () =>
              import('../login/lockout-screen/components/unlock-account/unlock-account.component').then((m) => m.UnlockAccountComponent),
          },
          {
            path: routes.loginVerificationFailed,
            canActivate: [TokenQueryParamAccessGuard],
            loadComponent: () =>
              import('../login/lockout-screen/components/verification-failed/verification-failed.component').then(
                (m) => m.VerificationFailedComponent,
              ),
          },
        ],
      },
      {
        path: '**',
        redirectTo: routes.login,
      },
    ],
  },
  {
    path: routes.forgotPassword,
    loadComponent: () =>
      import('../forgot-password/forgot-password-layout/forgot-password-layout.component').then((m) => m.ForgotPasswordLayoutComponent),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('../forgot-password/forgot-password-screen/forgot-password-screen.component').then((m) => m.ForgotPasswordScreenComponent),
      },
      {
        path: routes.resetPassword,
        canActivate: [TokenQueryParamAccessGuard],
        loadComponent: () =>
          import('../forgot-password/reset-password-screen/reset-password-screen.component').then((m) => m.ResetPasswordScreenComponent),
      },
    ],
  },
  {
    path: routes.getTheApp,
    loadComponent: () => import('../mobile-redirect-screen/mobile-redirect-screen.component').then((m) => m.MobileRedirectScreenComponent),
  },
  {
    path: routes.baaRequestFlow,
    canActivate: [MobileGuard, baaRequestTokenGuard],
    loadComponent: () => import('@libs/ffd-notes').then((m) => m.BaaRequestFlowComponent),
  },
  {
    path: routes.ffdNotesInvite,
    canActivate: [ffdNotesInvitationJoinRequestGuard],
    loadComponent: () => import('@libs/ffd-notes').then((m) => m.FfdNotesInvitationLandingComponent),
  },
  {
    path: routes.ffdNotesInviteSuccess,
    canActivate: [baaRequestTokenGuard],
    loadComponent: () => import('@libs/ffd-notes').then((m) => m.FfdNotesInviteSuccessComponent),
  },
  {
    path: routes.baaSignupFlow,
    canActivate: [baaRequestTokenGuard],
    loadComponent: () => import('@libs/ffd-notes').then((m) => m.BaaSignupFlowComponent),
    children: [
      {
        path: routes.baaSignupInfo,
        loadComponent: () => import('@libs/ffd-notes').then((m) => m.BaaSignupInfoComponent),
      },
      {
        path: routes.baaSignupAccountDetails,
        loadComponent: () => import('@libs/ffd-notes').then((m) => m.BaaSignupAccountDetailsComponent),
      },
      {
        path: routes.baaSignupOtp,
        loadComponent: () => import('@libs/ffd-notes').then((m) => m.BaaSignupOtpComponent),
      },
      {
        path: routes.baaSignupPracticeDetails,
        loadComponent: () => import('@libs/ffd-notes').then((m) => m.BaaSignupPracticeDetailsComponent),
      },
      {
        path: routes.baaSignupLogin,
        loadComponent: () => import('@libs/ffd-notes').then((m) => m.BaaSignupLoginComponent),
      },
      {
        path: '**',
        redirectTo: routes.baaSignupInfo,
      },
    ],
  },
  {
    path: '',
    loadComponent: () => import('@libs/ffd-notes').then((m) => m.FfdNotesLayoutComponent),
    canActivate: [LoggedInGuard, MobileGuard],
    children: [
      {
        path: routes.myAccount,
        canActivate: [MobileGuard],
        loadComponent: () => import('@libs/my-account').then((m) => m.MyAccountScreenComponent),
      },
      {
        path: routes.ffdNotesSignBaa,
        canActivate: [baaSignedGuard],
        loadComponent: () => import('@libs/ffd-notes').then((m) => m.FfdNotesSignBaaComponent),
      },
      {
        path: routes.ffdNotesActivate,
        canActivate: [activateGuard],
        loadComponent: () => import('@libs/ffd-notes').then((m) => m.FfdNotesActivateComponent),
      },
      {
        path: '',
        canActivate: [signBaaGuard],
        loadComponent: () => import('@libs/ffd-notes').then((m) => m.FfdNotesScreenComponent),
      },
    ],
  },
  {
    // Alias for the FFD Notes activation deep link / QR code
    path: 'mobile-notes/baa-activation',
    redirectTo: '',
  },
  {
    path: 'mobile-notes/baa-activation/:userId',
    redirectTo: '',
  },
  {
    // Alias for the FFD Notes BAA sign-up flow
    path: 'universal-notes/baa-sign',
    redirectTo: routes.ffdNotesSignBaa,
  },
  {
    path: 'mobile-notes/baa-sign',
    redirectTo: routes.ffdNotesSignBaa,
  },
  {
    // Alias for FFD Notes
    path: 'universal-notes',
    redirectTo: '',
  },
  {
    path: 'mobile-notes',
    redirectTo: '',
  },
];

export const appRoutes: Route[] = window.location.hostname === 'secure.ffdnotes.ai' ? notesOnlyRoutes : mainRoutes;
